import { useCallback } from 'react';
import styled from 'styled-components';

import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { DoubleArrow } from 'ui/icons/navigation/DoubleArrow';
import { DownArrow } from 'ui/icons/navigation/DownArrow';
import { UpArrow } from 'ui/icons/navigation/UpArrow';
import { button, colors, spacing } from 'ui/theme';
import { useSortingContext } from 'utils/contexts/sorting';

import { Cell } from './Cell';

type HeaderCellProps = {
  columnId: string;
  label: string;
  isSortable?: boolean;
  initialSortDirection?: SORT_DIRECTIONS;
};

const Wrapper = styled(Cell).attrs((props: { isSortable: boolean }) => ({
  as: props.isSortable ? 'button' : 'div',
}))<{
  isActiveSortColumn: boolean;
  columnId: string;
  isSortable: boolean;
}>`
  && {
    ${button}
    background: none;
    border: none;
    outline: none;
    white-space: nowrap;
    border-bottom: ${spacing.single} solid ${colors.grey15};

    span {
      display: inline-block;
      margin-right: ${spacing.quarter};
    }
    text-align: left;
    color: ${(props) => (props.isActiveSortColumn ? colors.brandPurple : colors.textPrimary)};
    align-items: center;
    justify-content: flex-start;
    flex-direction: row;
  }
`;

export const HeaderCell = ({
  columnId,
  isSortable,
  label,
  initialSortDirection,
}: HeaderCellProps) => {
  const { setSortBy, setSortDirection, sortBy, sortDirection } = useSortingContext();
  const isAscendingSort = sortDirection === SORT_DIRECTIONS.ASCENDING;
  const isActiveSortColumn = sortBy === columnId;
  const handleClick = useCallback(() => {
    if (isActiveSortColumn) {
      setSortDirection(isAscendingSort ? SORT_DIRECTIONS.DESCENDING : SORT_DIRECTIONS.ASCENDING);
    } else if (isSortable) {
      setSortBy(columnId);
      setSortDirection(initialSortDirection || SORT_DIRECTIONS.DESCENDING);
    }
  }, [
    setSortDirection,
    setSortBy,
    isSortable,
    columnId,
    isAscendingSort,
    isActiveSortColumn,
    initialSortDirection,
  ]);

  return (
    <Wrapper
      data-test={`column-header-${columnId}`}
      isActiveSortColumn={isActiveSortColumn}
      isSortable={!!isSortable}
      columnId={columnId}
      key={columnId}
      onClick={handleClick}
    >
      <span>{label}</span>
      {isSortable && !isActiveSortColumn && <DoubleArrow isColorInherited />}
      {isSortable && isActiveSortColumn && isAscendingSort && <UpArrow isColorInherited />}
      {isSortable && isActiveSortColumn && !isAscendingSort && <DownArrow isColorInherited />}
    </Wrapper>
  );
};
